import { Dispatch, SetStateAction } from "react";

import { Column, Icon, Row, TagText, TagTextAlert, Text, StatProps, TextVariant } from "@bridgesplit/ui";
import {
    AppButton,
    BalanceDisplay,
    GetAssetButton,
    PendingLeverageStatsCard,
    useLoanOraclePriceQuotes
} from "app/components/common";
import { MaxButton, TokenInputWithPrice } from "app/components/common/util";
import { StrategyDuration } from "@bridgesplit/abf-sdk";
import {
    AbfLoanExpanded,
    getLoopFirstAddCollateralEvent,
    getZcCollateral,
    getZcLedger,
    LoopPositionExpanded,
    useOraclePrices,
    calculateCurrentLtv,
    formatLeverage,
    calculateHealthRatio,
    getZcLoanLiquidationThreshold,
    DerivedLoanEventActionType,
    isEventActionMetadataType,
    ParsedLoanEventExpanded
} from "@bridgesplit/abf-react";
import { bsMath, roundToDecimals, formatPercent, formatTokenAmount } from "@bridgesplit/utils";
import { ArrowForwardOutlined } from "@mui/icons-material";

import { useManualIncreaseLeverageController } from "./useManualIncreaseLeverageController";
import { LoanPositionStats, usePositionData } from "./LoanPositionStats";
export function ManualIncreaseLeverage({
    loanExpanded,
    strategyDuration,
    setIsSwapOpen,
    loopPosition
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    strategyDuration?: StrategyDuration;
    setIsSwapOpen?: Dispatch<SetStateAction<boolean>>;
    loopPosition: LoopPositionExpanded | undefined;
}) {
    const c = useManualIncreaseLeverageController({ loanExpanded, strategyDuration });
    const collateral = getZcCollateral(loanExpanded);
    const collateralMetadata = collateral?.metadata;
    const principalMetadata = loanExpanded?.principalMetadata;
    const ledger = getZcLedger(loanExpanded);
    const originalPrincipalAmount = ledger?.ledgerDebt.total ?? 0;
    const originalCollateralAmount = collateral?.amount ?? 0;
    const newPrincipalAmount = bsMath.add(originalPrincipalAmount, c.borrow.value ?? 0) ?? 0;
    const newCollateralAmount = bsMath.add(originalCollateralAmount, c.collateral.value ?? 0) ?? 0;

    return (
        <Column spacing={3}>
            <Column spacing={3}>
                <Column spacing={0.5}>
                    <Row spaceBetween>
                        <Column>
                            <Text variant="body2" color="caption">
                                Add Collateral
                            </Text>
                        </Column>
                        <GetAssetButton
                            buyToken={collateralMetadata}
                            sellToken={principalMetadata}
                            swapOnClick={!setIsSwapOpen ? undefined : () => setIsSwapOpen(true)}
                        />
                    </Row>
                    <TokenInputWithPrice
                        value={c.collateral.value}
                        metadata={c.collateral.metadata}
                        selectToken={null}
                        setValue={c.collateral.setValue}
                        hideUsdPrice={false}
                        belowInput={
                            <BalanceDisplay
                                isLoading={c.collateral.balanceLoading}
                                balance={c.collateral.balance}
                                metadata={c.collateral.metadata}
                                setMax={c.collateral.setMax}
                                insufficientBalance={c.collateral.insufficientBalance}
                            />
                        }
                    />
                    {c.collateral.warning && (
                        <TagTextAlert color={c.collateral.warning.color}>{c.collateral.warning.message}</TagTextAlert>
                    )}
                </Column>
                <Column spacing={0.5}>
                    <Column>
                        <Text variant="body2" color="caption">
                            Borrow More
                        </Text>
                    </Column>
                    <TokenInputWithPrice
                        metadata={c.borrow.metadata}
                        value={c.borrow.value}
                        setValue={c.borrow.setValue}
                        selectToken={null}
                        disabled={!c.borrow.available}
                        loading={c.borrow.loading}
                        belowInput={
                            <Row spaceBetween>
                                <BalanceDisplay
                                    isLoading={c.borrow.loading}
                                    balance={c.borrow.available ?? 0}
                                    metadata={c.borrow.metadata}
                                    insufficientBalance={false}
                                    maxText="Available"
                                    hideRowEnd
                                />

                                <BorrowAmountPresets
                                    value={c.borrow.value}
                                    maxValue={c.borrow.available ?? 0}
                                    onNoBorrow={() => c.borrow.setValue(0)}
                                    onMax={() => c.borrow.setMax()}
                                />
                            </Row>
                        }
                    />
                </Column>
                {c.borrow.warning && (
                    <TagTextAlert color={c.borrow.warning.color}>{c.borrow.warning.message}</TagTextAlert>
                )}
            </Column>

            <LoanPositionStats
                loanExpanded={loanExpanded}
                loopPosition={loopPosition}
                newPrincipalAmount={newPrincipalAmount}
                newCollateralAmount={newCollateralAmount}
                isFullRepay={false}
                loading={c.collateral.balanceLoading || c.borrow.loading}
                show={{
                    debtAndCollateral: true,
                    health: !!(c.collateral.value || c.borrow.value),
                    netApyCollapsable: !!(c.collateral.value || c.borrow.value)
                }}
            />

            <AppButton isTransaction fullWidth sx={{ mt: 1 }} {...c.buttonProps} />

            <PendingLeverageWarning
                loanExpanded={loanExpanded}
                collateralWalletBalance={c.collateral.balance}
                collateralInputDepositAmount={c.collateral.value}
                principalInputBorrowAmount={c.borrow.value}
            />
        </Column>
    );
}

const BORROW_PRESETS = ["No Borrow", "Max"] as const;

function BorrowAmountPresets({
    value,
    maxValue,
    onNoBorrow,
    onMax
}: {
    value: number | undefined;
    maxValue: number;
    onNoBorrow: () => void;
    onMax: () => void;
}) {
    const isMaxSelected = maxValue > 0 && value === maxValue;

    return (
        <Row spacing={0.5}>
            {BORROW_PRESETS.map((preset) => {
                const isNoBorrow = preset === "No Borrow";
                const isSelected = isNoBorrow ? false : isMaxSelected;
                const disabled = isNoBorrow ? false : maxValue <= 0;

                return (
                    <MaxButton
                        key={preset}
                        disabled={disabled}
                        onClick={isNoBorrow ? onNoBorrow : onMax}
                        isSelected={isSelected}
                        textSx={{ whiteSpace: "nowrap", lineHeight: 1 }}
                    >
                        {preset}
                    </MaxButton>
                );
            })}
        </Row>
    );
}

function useCalculateFinalExpectedCollateralAmount({
    originalCollateralAmount,
    collateralInputDepositAmount,
    principalInputBorrowAmount,
    principalUsdOraclePrice,
    collateralUsdOraclePrice
}: {
    originalCollateralAmount: number | undefined;
    collateralInputDepositAmount: number | undefined;
    principalInputBorrowAmount: number | undefined;
    principalUsdOraclePrice: number | undefined;
    collateralUsdOraclePrice: number | undefined;
}): number | undefined {
    // principalInputBorrowAmount is assumed to be in UI units.
    const principalInputUsdMarketValue = bsMath.mul(principalInputBorrowAmount ?? 0, principalUsdOraclePrice ?? 0);
    const expectedCollateralFromSwap = bsMath.div(principalInputUsdMarketValue, collateralUsdOraclePrice ?? 0);
    const finalExpectedCollateralAmount = bsMath.add(
        expectedCollateralFromSwap,
        originalCollateralAmount,
        collateralInputDepositAmount ?? 0
    );
    return finalExpectedCollateralAmount;
}

function PendingLeverageStats({
    loanExpanded,
    collateralInputDepositAmount,
    principalInputBorrowAmount
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    collateralInputDepositAmount: number | undefined;
    principalInputBorrowAmount: number | undefined;
}) {
    const collateral = getZcCollateral(loanExpanded);
    const collateralMetadata = collateral?.metadata;
    const principalMetadata = loanExpanded?.principalMetadata;

    const originalCollateralAmount = collateral?.amount ?? 0;
    const ledger = getZcLedger(loanExpanded);
    const originalPrincipalAmount = ledger?.ledgerDebt.total ?? 0;
    const { principalUsdOraclePrice, collateralUsdOraclePrice } = useLoanOraclePriceQuotes(
        loanExpanded?.principalMetadata,
        collateral
    );

    const base = usePositionData({
        loanExpanded,
        newPrincipalAmount: bsMath.add(originalPrincipalAmount, principalInputBorrowAmount ?? 0) ?? 0,
        newCollateralAmount: bsMath.add(originalCollateralAmount, collateralInputDepositAmount ?? 0) ?? 0,
        isFullRepay: false
    });

    const liquidationThreshold = getZcLoanLiquidationThreshold(loanExpanded);

    const healthBeforeSwap = base?.newHealth?.health;
    const ltvBeforeSwap = base?.newLtv;

    const stats = useGetPendingLeverageStats({
        collateralInputDepositAmount,
        principalInputBorrowAmount,
        healthBeforeSwap,
        ltvBeforeSwap,
        liquidationThreshold,
        originalPrincipalAmount,
        originalCollateralAmount,
        principalUsdOraclePrice,
        collateralUsdOraclePrice
    });

    const helpText = `Expected leverage after you swap the borrowed ${formatTokenAmount(principalInputBorrowAmount, {
        symbol: principalMetadata?.symbol,
        decimals: principalMetadata?.decimals
    })} and deposit ${collateralMetadata?.symbol}`;

    return <PendingLeverageStatsCard title="New Pending Leverage" helpText={helpText} stats={stats} />;
}

export type PendingLeverageStatsArgs = {
    originalPrincipalAmount: number | undefined;
    originalCollateralAmount: number | undefined;
    collateralInputDepositAmount: number | undefined;
    principalInputBorrowAmount: number | undefined;
    healthBeforeSwap: number | undefined;
    ltvBeforeSwap: number | undefined;
    liquidationThreshold: number | undefined;
    principalUsdOraclePrice: number | undefined;
    collateralUsdOraclePrice: number | undefined;
    variant?: TextVariant;
};

export function useGetPendingLeverageStats({
    originalPrincipalAmount,
    originalCollateralAmount,
    collateralInputDepositAmount,
    principalInputBorrowAmount,
    healthBeforeSwap,
    ltvBeforeSwap,
    liquidationThreshold,
    principalUsdOraclePrice,
    collateralUsdOraclePrice,
    variant = "body2"
}: PendingLeverageStatsArgs): StatProps[] {
    const newCollateralAmount = useCalculateFinalExpectedCollateralAmount({
        originalCollateralAmount,
        collateralInputDepositAmount,
        principalInputBorrowAmount,
        principalUsdOraclePrice,
        collateralUsdOraclePrice
    });

    const newPrincipalAmount = bsMath.add(originalPrincipalAmount, principalInputBorrowAmount ?? 0);

    const totalOwedValue = bsMath.mul(newPrincipalAmount, principalUsdOraclePrice ?? 0);
    const loanCollateralValue = bsMath.mul(newCollateralAmount, collateralUsdOraclePrice ?? 0);

    const pendingLtv = calculateCurrentLtv({
        totalOwedValue,
        loanCollateralValue
    });
    const leverageMultiplier = 1 / (1 - pendingLtv);

    const healthAfterExpectedSwap = calculateHealthRatio(totalOwedValue, loanCollateralValue, liquidationThreshold);

    return [
        { caption: "Leverage", value: formatLeverage(leverageMultiplier) },
        {
            caption: "Health",
            value: (
                <ChangeValue
                    variant={variant}
                    originalValue={formatPercent(healthBeforeSwap)}
                    newValue={formatPercent(healthAfterExpectedSwap)}
                />
            )
        },
        {
            caption: "Position LTV",
            value: (
                <ChangeValue
                    variant={variant}
                    originalValue={formatPercent(ltvBeforeSwap)}
                    newValue={formatPercent(pendingLtv)}
                />
            )
        }
    ];
}

function ChangeValue({
    originalValue,
    newValue,
    variant = "body1"
}: {
    originalValue: string | undefined;
    newValue: string | undefined;
    variant?: TextVariant;
}) {
    return (
        <Row sx={{ minWidth: "max-content" }} spacing={0.5}>
            <Text variant={variant} color="caption">
                {originalValue}
            </Text>
            <Text color="disabled" variant="body1">
                <ArrowForwardOutlined />
            </Text>
            <Text variant={variant} color="body">
                {newValue}
            </Text>
        </Row>
    );
}

function PendingLeverageWarning({
    loanExpanded,
    collateralWalletBalance,
    collateralInputDepositAmount,
    principalInputBorrowAmount
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    collateralWalletBalance: number | undefined;
    collateralInputDepositAmount: number | undefined;
    principalInputBorrowAmount: number | undefined;
}) {
    const previouslyIncreasedLeveraged = hasIncreasedLeverage({ loanExpanded });
    const hasDepositInput = !!collateralInputDepositAmount;
    const hasBorrowInput = !!principalInputBorrowAmount;
    const hasAnyInput = hasDepositInput || hasBorrowInput;
    const shouldShowPendingWarning = !previouslyIncreasedLeveraged && !hasAnyInput;
    const shouldShowBorrowPendingStats = hasBorrowInput; // borrow-only OR deposit+borrow
    const shouldShowDepositOnlyPreview = hasDepositInput && !hasBorrowInput;

    if (shouldShowPendingWarning) {
        return (
            <TagText
                textVariant="body1"
                fullWidth
                color="warning"
                helpText={getIncreaseLeverageHelpMessage({ loanExpanded, collateralWalletBalance })}
                px={1.5}
                py={1}
            >
                Pending leverage
                <Icon type="warning" />
            </TagText>
        );
    }
    if (shouldShowBorrowPendingStats) {
        return (
            <PendingLeverageStats
                loanExpanded={loanExpanded}
                collateralInputDepositAmount={collateralInputDepositAmount}
                principalInputBorrowAmount={principalInputBorrowAmount}
            />
        );
    }
    if (shouldShowDepositOnlyPreview) {
        return (
            <LeverageAfterCollateralDeposit
                loanExpanded={loanExpanded}
                collateralInputDepositAmount={collateralInputDepositAmount}
            />
        );
    }
    return null;
}

function LeverageAfterCollateralDeposit({
    loanExpanded,
    collateralInputDepositAmount
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    collateralInputDepositAmount: number | undefined;
}) {
    const collateral = getZcCollateral(loanExpanded);
    const ledger = getZcLedger(loanExpanded);
    const { getOracle } = useOraclePrices([loanExpanded?.principalMetadata?.mint, collateral?.metadata?.mint]);
    const principalOracle = getOracle(loanExpanded?.principalMetadata?.mint);
    const collateralOracle = getOracle(collateral?.metadata?.mint);
    const originalPrincipalAmount = ledger?.ledgerDebt.total ?? 0;
    const originalCollateralAmount = collateral?.amount ?? 0;
    // Deposit-only preview: debt stays constant, only collateral input is added.
    const totalOwedValue = bsMath.mul(originalPrincipalAmount, principalOracle?.usdPrice ?? 0) ?? 0;
    const collateralAmountAfterDeposit = bsMath.add(originalCollateralAmount, collateralInputDepositAmount ?? 0) ?? 0;
    const loanCollateralValue = bsMath.mul(collateralAmountAfterDeposit, collateralOracle?.usdPrice ?? 0) ?? 0;
    const pendingLtv = calculateCurrentLtv({
        totalOwedValue,
        loanCollateralValue
    });
    const leverageMultiplier = 1 / (1 - pendingLtv);

    return (
        <TagTextAlert color="info" variant="body2" icon="check">
            {`This deposit will set your position leverage to ${formatLeverage(leverageMultiplier)}`}
        </TagTextAlert>
    );
}

function getIncreaseLeverageHelpMessage({
    loanExpanded,
    collateralWalletBalance
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    collateralWalletBalance: number | undefined;
}): string {
    const collateral = getZcCollateral(loanExpanded);
    const collateralMetadata = collateral?.metadata;
    const principalMetadata = loanExpanded?.principalMetadata;

    // if (collateralWalletBalance && collateralWalletBalance > 0) {
    //     return `Your position is pending leverage. Deposit collateral to leverage your position.`;
    // }

    return `Your position is pending leverage. Swap borrowed ${principalMetadata?.symbol} to ${collateralMetadata?.symbol} to deposit leveraged collateral into your position.`;
}

function hasIncreasedLeverage({ loanExpanded }: { loanExpanded: AbfLoanExpanded | undefined }): boolean {
    const collateral = getZcCollateral(loanExpanded);
    const collateralDecimals = collateral?.metadata.decimals;

    const initialUserCollateralContribution = loanExpanded?.pnlStats?.initialUserCollateralContribution ?? 0;
    if (!initialUserCollateralContribution) return false;
    const firstAddCollateralEvent = getLoopFirstAddCollateralEvent(loanExpanded?.events);
    const firstAddCollateralEventAmount = firstAddCollateralEvent?.amount ?? 0;

    const initialContributedRounded = roundToDecimals(initialUserCollateralContribution, collateralDecimals);
    const firstAddCollateralEventAmountRounded = roundToDecimals(firstAddCollateralEventAmount, collateralDecimals);

    const eventsSansFirstTwo = loanExpanded?.events.slice(2);

    const hasAdditionalAddCollateralEvents = eventsSansFirstTwo?.some((event) =>
        isEventActionMetadataType(event.actionMetadata, DerivedLoanEventActionType.AddCollateral)
    );

    if (hasAdditionalAddCollateralEvents) {
        return true;
    }

    const addCollateralAndBorrowMoreEvents =
        eventsSansFirstTwo?.filter(
            (event) =>
                isEventActionMetadataType(event.actionMetadata, DerivedLoanEventActionType.AddCollateral) ||
                isEventActionMetadataType(event.actionMetadata, DerivedLoanEventActionType.BorrowPrincipal)
        ) ?? [];

    const eventsGroupedByTxn = groupParsedEventsByEventTxn(addCollateralAndBorrowMoreEvents);

    if (eventsGroupedByTxn.size > 0) {
        return true;
    }

    if (initialContributedRounded !== firstAddCollateralEventAmountRounded) {
        return true;
    }
    return false;

    // at this point we know its either a loan or a manual loop;
    // we know the first two events are borrow more and add collateral regardless
    // excluding those if there is a matching borrow more and add collateral event, then it was a flash borrow and mustve added leverage
}

function groupParsedEventsByEventTxn(events: ParsedLoanEventExpanded[]): Map<string, ParsedLoanEventExpanded[]> {
    const eventsByTxn = new Map<string, ParsedLoanEventExpanded[]>();

    events.forEach((event) => {
        const txnEvents = eventsByTxn.get(event.eventTxn) || [];
        txnEvents.push(event);
        eventsByTxn.set(event.eventTxn, txnEvents);
    });

    return eventsByTxn;
}
